import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import InlineFeedback from '../components/InlineFeedback';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost === '::1' || currentHost === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;

const todayString = () => new Date().toISOString().split('T')[0];
const daysAgoString = (days) => {
  const value = new Date();
  value.setDate(value.getDate() - days);
  return value.toISOString().split('T')[0];
};

const formatMoney = (centsValue) => `$${((Number(centsValue || 0) || 0) / 100).toFixed(2)}`;

const OwnerInsightsGovernance = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [filters, setFilters] = useState({
    startDate: daysAgoString(29),
    endDate: todayString(),
    comparePrevious: true,
  });
  const [analytics, setAnalytics] = useState(null);
  const [governance, setGovernance] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [isSavingGovernance, setIsSavingGovernance] = useState(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadInsights = async () => {
    if (!token) return;
    try {
      setIsLoading(true);
      const [analyticsRes, governanceRes, auditRes] = await Promise.all([
        axios.get(apiUrl('userprofiles/owner/analytics/'), {
          headers: authHeaders,
          params: {
            start_date: filters.startDate,
            end_date: filters.endDate,
            compare_previous: filters.comparePrevious,
          },
        }),
        axios.get(apiUrl('userprofiles/owner/governance/'), { headers: authHeaders }),
        axios.get(apiUrl('userprofiles/owner/audit-timeline/'), { headers: authHeaders, params: { limit: 50 } }),
      ]);
      setAnalytics(analyticsRes.data || null);
      setGovernance(governanceRes.data || null);
      setAuditEvents(auditRes.data || []);
      setFeedback({ type: '', message: '' });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error?.response?.data?.error || 'Unable to load owner insights and governance data.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleApplyFilters = async () => {
    try {
      setIsApplyingFilters(true);
      const analyticsRes = await axios.get(apiUrl('userprofiles/owner/analytics/'), {
        headers: authHeaders,
        params: {
          start_date: filters.startDate,
          end_date: filters.endDate,
          compare_previous: filters.comparePrevious,
        },
      });
      setAnalytics(analyticsRes.data || null);
      setFeedback({ type: '', message: '' });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error?.response?.data?.error || 'Unable to apply analytics filters.',
      });
    } finally {
      setIsApplyingFilters(false);
    }
  };

  const handleGovernanceChange = (field, value) => {
    setGovernance((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handleSaveGovernance = async () => {
    if (!governance) return;
    try {
      setIsSavingGovernance(true);
      const payload = {
        require_deposit: Boolean(governance.require_deposit),
        deposit_percent: Number(governance.deposit_percent || 0),
        cancellation_cutoff_hours: governance.cancellation_cutoff_hours === '' ? null : Number(governance.cancellation_cutoff_hours),
        reschedule_cutoff_hours: governance.reschedule_cutoff_hours === '' ? null : Number(governance.reschedule_cutoff_hours),
        workers_manage_own_schedule: Boolean(governance.workers_manage_own_schedule),
        workers_manage_own_bookings: Boolean(governance.workers_manage_own_bookings),
        owner_override_enabled: Boolean(governance.owner_override_enabled),
      };
      const response = await axios.patch(apiUrl('userprofiles/owner/governance/'), payload, { headers: authHeaders });
      setGovernance(response.data || null);
      setFeedback({ type: 'success', message: 'Governance settings saved.' });
      const auditRes = await axios.get(apiUrl('userprofiles/owner/audit-timeline/'), { headers: authHeaders, params: { limit: 50 } });
      setAuditEvents(auditRes.data || []);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error?.response?.data?.error || 'Unable to save governance settings.',
      });
    } finally {
      setIsSavingGovernance(false);
    }
  };

  const kpiRows = [
    { key: 'captured_revenue_cents', label: 'Captured Revenue', formatter: (value) => formatMoney(value) },
    { key: 'expected_deposits_cents', label: 'Expected Deposits', formatter: (value) => formatMoney(value) },
    { key: 'paid_booking_conversion_rate', label: 'Paid Booking Conversion', formatter: (value) => `${Number(value || 0).toFixed(2)}%` },
    { key: 'cancellation_rate', label: 'Cancellation Rate', formatter: (value) => `${Number(value || 0).toFixed(2)}%` },
    { key: 'total_bookings', label: 'Total Bookings', formatter: (value) => Number(value || 0).toFixed(0) },
    { key: 'active_workers', label: 'Active Workers', formatter: (value) => Number(value || 0).toFixed(0) },
  ];

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F6F4F8] p-6">
        <InlineFeedback type="error" message="Please log in to access owner insights." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F4F8] px-3 py-4 text-[#2E2E2E] sm:px-4 sm:py-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="rounded-2xl bg-gradient-to-r from-[#1F2937] to-[#374151] p-4 text-white shadow-lg sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold sm:text-3xl">Owner Insights &amp; Governance</h1>
              <p className="mt-2 text-sm text-slate-200">Company-scoped KPIs, policy controls, and audit visibility.</p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/owner-dashboard')}
              className="rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/25"
            >
              Back to Owner Dashboard
            </button>
          </div>
        </header>

        {feedback.message ? <InlineFeedback type={feedback.type || 'info'} message={feedback.message} /> : null}
        {isLoading ? <InlineFeedback type="info" message="Loading owner insights..." /> : null}

        <section className="rounded-2xl bg-white p-5 shadow-sm space-y-3">
          <h2 className="text-lg font-semibold text-[#111827]">Analytics Filters</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <input
              aria-label="Start date"
              type="date"
              value={filters.startDate}
              onChange={(event) => setFilters((prev) => ({ ...prev, startDate: event.target.value }))}
              className="rounded border px-3 py-2 text-sm"
            />
            <input
              aria-label="End date"
              type="date"
              value={filters.endDate}
              onChange={(event) => setFilters((prev) => ({ ...prev, endDate: event.target.value }))}
              className="rounded border px-3 py-2 text-sm"
            />
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.comparePrevious}
                onChange={(event) => setFilters((prev) => ({ ...prev, comparePrevious: event.target.checked }))}
              />
              Compare previous range
            </label>
            <button
              type="button"
              disabled={isApplyingFilters}
              onClick={handleApplyFilters}
              className="rounded bg-[#1D4ED8] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1E40AF]"
            >
              Apply Filters
            </button>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {kpiRows.map((metric) => {
            const value = analytics?.kpis?.[metric.key] || null;
            return (
              <article key={metric.key} className="rounded-2xl bg-white p-5 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-gray-500">{metric.label}</p>
                <p className="mt-2 text-2xl font-bold text-[#111827]">{metric.formatter(value?.current)}</p>
                {analytics?.comparison?.enabled ? (
                  <p className="mt-1 text-xs text-gray-600">
                    Prev: {metric.formatter(value?.previous)} • Δ {value?.delta_percent === null ? 'N/A' : `${Number(value?.delta_percent).toFixed(2)}%`}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-gray-600">Comparison disabled</p>
                )}
              </article>
            );
          })}
        </section>

        <section className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl bg-white p-5 shadow-sm space-y-3">
            <h2 className="text-lg font-semibold text-[#111827]">Governance Settings</h2>
            {!governance ? (
              <p className="text-sm text-gray-500">No governance settings available yet.</p>
            ) : (
              <>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(governance.require_deposit)}
                    onChange={(event) => handleGovernanceChange('require_deposit', event.target.checked)}
                  />
                  Require deposit payment
                </label>
                <input
                  aria-label="Deposit percent"
                  type="number"
                  min="0"
                  max="100"
                  value={governance.deposit_percent ?? 0}
                  onChange={(event) => handleGovernanceChange('deposit_percent', event.target.value)}
                  className="w-full rounded border px-3 py-2 text-sm"
                  placeholder="Deposit percent"
                />
                <input
                  aria-label="Cancellation cutoff"
                  type="number"
                  min="0"
                  value={governance.cancellation_cutoff_hours ?? ''}
                  onChange={(event) => handleGovernanceChange('cancellation_cutoff_hours', event.target.value)}
                  className="w-full rounded border px-3 py-2 text-sm"
                  placeholder="Cancellation cutoff hours (blank uses default)"
                />
                <input
                  aria-label="Reschedule cutoff"
                  type="number"
                  min="0"
                  value={governance.reschedule_cutoff_hours ?? ''}
                  onChange={(event) => handleGovernanceChange('reschedule_cutoff_hours', event.target.value)}
                  className="w-full rounded border px-3 py-2 text-sm"
                  placeholder="Reschedule cutoff hours (blank uses default)"
                />
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(governance.workers_manage_own_schedule)}
                    onChange={(event) => handleGovernanceChange('workers_manage_own_schedule', event.target.checked)}
                  />
                  Workers manage own schedule
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(governance.workers_manage_own_bookings)}
                    onChange={(event) => handleGovernanceChange('workers_manage_own_bookings', event.target.checked)}
                  />
                  Workers manage own bookings
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(governance.owner_override_enabled)}
                    onChange={(event) => handleGovernanceChange('owner_override_enabled', event.target.checked)}
                  />
                  Owner operational override enabled
                </label>
                <button
                  type="button"
                  disabled={isSavingGovernance}
                  onClick={handleSaveGovernance}
                  className="rounded bg-[#2F855A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#276749]"
                >
                  Save Governance Settings
                </button>
              </>
            )}
          </div>

          <div className="rounded-2xl bg-white p-5 shadow-sm space-y-3">
            <h2 className="text-lg font-semibold text-[#111827]">Audit Timeline</h2>
            {auditEvents.length === 0 ? (
              <p className="text-sm text-gray-500">No audit events in this scope.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {auditEvents.map((event) => (
                  <li key={`${event.event_type}-${event.event_id}`} className="rounded border border-gray-200 p-3">
                    <p className="font-semibold text-[#1F2937]">{event.event_type} • {event.action}</p>
                    <p className="text-xs text-gray-600">
                      By {event.actor_username || 'system'} at {event.created_at}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default OwnerInsightsGovernance;
